import React, { useEffect, useRef, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { XIcon, CheckIcon, CopyIcon, GlobeIcon, Loader2Icon, ShieldCheckIcon, ChevronDownIcon, ExternalLinkIcon, AlertCircleIcon, LifeBuoyIcon } from 'lucide-react';

/* ------------------------------------------------------------------ Domain */

type DomainKind = 'subdomain' | 'root';

/** Root domains have no host label of their own (easylease.ae, easylease.co.uk). */
function domainKind(domain: string): DomainKind {
  const labels = domain.split('.');
  if (labels.length <= 2) return 'root';
  const secondLast = labels[labels.length - 2];
  if (labels.length === 3 && ['co', 'com', 'net', 'org', 'gov', 'ac'].includes(secondLast)) return 'root';
  return 'subdomain';
}
function normalise(input: string): string {
  return input.trim().toLowerCase().replace(/^https?:\/\//, '').replace(/^www\./, '').replace(/\/.*$/, '');
}
function isValid(domain: string): boolean {
  return /^[a-z0-9-]+(\.[a-z0-9-]+)+$/.test(domain) && domain.length > 3;
}
interface DnsRecord {
  type: string;
  host: string;
  value: string;
}

/**
 * The connection record the platform generates for this address. Subdomains
 * point with a CNAME; root domains need a flattening ALIAS/ANAME record.
 */
function dnsRecordFor(domain: string): DnsRecord {
  const kind = domainKind(domain);
  if (kind === 'root') return {
    type: 'ALIAS / ANAME',
    host: '@',
    value: 'shops.talab.app'
  };
  return {
    type: 'CNAME',
    host: domain.split('.')[0],
    value: 'shops.talab.app'
  };
}

/* ------------------------------------------------------------------- Checks */

type CheckState = 'waiting' | 'pending' | 'done';
interface Checks {
  dns: CheckState;
  ownership: CheckState;
  ssl: CheckState;
  shop: CheckState;
}
const EMPTY_CHECKS: Checks = {
  dns: 'waiting',
  ownership: 'waiting',
  ssl: 'waiting',
  shop: 'waiting'
};
const DONE_CHECKS: Checks = {
  dns: 'done',
  ownership: 'done',
  ssl: 'done',
  shop: 'done'
};
type Phase = 'form' | 'checking' | 'connected' | 'notfound' | 'taken';
interface DomainModalProps {
  /** The connected custom domain, empty when the shop uses its Talab link. */
  current: string;
  talabUrl: string;
  shopHref: string;
  onClose: () => void;
  onSave: (domain: string) => void;
}

/**
 * Manage domain. Simple by default — pick an address, connect, done. Every
 * DNS value and technical status lives behind an optional Advanced section so
 * a non-technical merchant never has to see it.
 */
export function DomainModal({
  current,
  talabUrl,
  shopHref,
  onClose,
  onSave
}: DomainModalProps) {
  const [mode, setMode] = useState<'talab' | 'own'>(current ? 'own' : 'talab');
  const [domain, setDomain] = useState(current);
  const [phase, setPhase] = useState<Phase>(current ? 'connected' : 'form');
  const [checks, setChecks] = useState<Checks>(current ? DONE_CHECKS : EMPTY_CHECKS);
  const [advanced, setAdvanced] = useState(false);
  const [error, setError] = useState('');
  const [copied, setCopied] = useState('');
  const timers = useRef<number[]>([]);
  useEffect(() => () => {
    timers.current.forEach(window.clearTimeout);
  }, []);
  const clean = normalise(domain);
  const record = dnsRecordFor(clean || 'shop.yourbusiness.com');
  const copy = (key: string, text: string) => {
    navigator.clipboard?.writeText(text);
    setCopied(key);
    window.setTimeout(() => setCopied(''), 1600);
  };

  /** Talab checks the connection itself — the merchant just waits. */
  const runChecks = () => {
    timers.current.forEach(window.clearTimeout);
    timers.current = [];
    setError('');
    setPhase('checking');
    setChecks({
      dns: 'pending',
      ownership: 'waiting',
      ssl: 'waiting',
      shop: 'waiting'
    });

    // A domain that's live somewhere else can never connect here.
    if (clean.includes('taken')) {
      timers.current.push(window.setTimeout(() => {
        setPhase('taken');
        setChecks(EMPTY_CHECKS);
      }, 1400));
      return;
    }
    const steps: Array<[number, Checks]> = [[900, {
      dns: 'done',
      ownership: 'pending',
      ssl: 'waiting',
      shop: 'waiting'
    }], [1800, {
      dns: 'done',
      ownership: 'done',
      ssl: 'pending',
      shop: 'waiting'
    }], [2900, {
      dns: 'done',
      ownership: 'done',
      ssl: 'done',
      shop: 'pending'
    }], [3700, DONE_CHECKS]];
    steps.forEach(([delay, next]) => timers.current.push(window.setTimeout(() => setChecks(next), delay)));
    timers.current.push(window.setTimeout(() => {
      setPhase('connected');
      onSave(clean);
    }, 3900));
  };
  const connect = () => {
    if (!isValid(clean)) {
      setError('Enter a valid domain, for example shop.yourbusiness.com.');
      return;
    }
    runChecks();
  };
  const statusLine = checks.shop === 'done' ? 'Ready' : checks.ssl !== 'waiting' && checks.ssl !== 'done' ? 'Securing your domain…' : checks.ssl === 'done' ? 'Finishing up…' : checks.ownership === 'pending' ? 'Verifying your domain…' : 'Checking connection…';

  /* --------------------------------------------------------------- Render */
  return <div className="fixed inset-0 z-[70] flex items-center justify-center p-4 bg-dark-navy/50 backdrop-blur-sm" data-id="element-3516" data-mp="3ejb2m9ketz3t">
      <motion.div initial={{
      opacity: 0,
      scale: 0.97
    }} animate={{
      opacity: 1,
      scale: 1
    }} transition={{
      duration: 0.18,
      ease: [0.23, 1, 0.32, 1]
    }} role="dialog" aria-label="Manage domain" className="w-full max-w-md bg-white rounded-3xl border border-slate-200 shadow-2xl max-h-[88vh] overflow-y-auto" data-id="element-3517" data-mp="3t8pebobgksmd">
        <div className="p-6" data-id="element-3518" data-mp="3ph99tk699cum">
          <div className="flex items-start justify-between gap-3 mb-5" data-id="element-3519" data-mp="3pgfzyac02789">
            <div data-id="element-3520" data-mp="3u8hlynyyzcbh">
              <h2 className="font-display font-bold text-lg text-dark-navy" data-id="element-3521" data-mp="3u7y1azhxq9tr">
                Manage domain
              </h2>
              <p className="text-xs text-slate-500 mt-1" data-id="element-3522" data-mp="31estqzkmvym">
                Choose the address customers use for your shop.
              </p>
            </div>
            <button onClick={onClose} aria-label="Close" className="p-1.5 rounded-lg text-slate-400 hover:text-dark-navy hover:bg-slate-50 transition-colors duration-150 ease-out" data-id="element-3523" data-mp="36i3oghxm8l9i">
              <XIcon className="w-4 h-4" data-id="element-3524" data-mp="3ghmad3x36h47" />
            </button>
          </div>

          {/* ------------------------------------------------ Success view */}
          {phase === 'connected' && mode === 'own' ? <div data-id="element-3525" data-mp="vor5n9fxjpeq">
              <div className="p-5 rounded-2xl bg-success/[0.06] border border-success/20" data-id="element-3526" data-mp="vsez68ldkbtg">
                <p className="inline-flex items-center gap-2 text-sm font-bold text-success" data-id="element-3527" data-mp="2gapji4ilw7wq">
                  <CheckIcon className="w-4 h-4" data-id="element-3528" data-mp="2bbmlh411n1y9" />
                  Your domain is connected
                </p>
                <p className="font-display font-bold text-dark-navy text-lg mt-2 break-all" data-id="element-3529" data-mp="26ggtpokymt29">
                  {clean}
                </p>
                <p className="text-xs text-slate-500 mt-1" data-id="element-3530" data-mp="1xierx6tejwdx">
                  This is now your primary shop address.
                </p>
                <p className="inline-flex items-center gap-1.5 text-[11px] font-bold text-success mt-3" data-id="element-3531" data-mp="1shqti02ugd9o">
                  <ShieldCheckIcon className="w-3.5 h-3.5" data-id="element-3532" data-mp="1nm1rlq3cqka7" />
                  Secure connection active
                </p>
              </div>

              <div className="flex items-center justify-between gap-3 mt-3 p-4 rounded-2xl bg-slate-50 border border-slate-100" data-id="element-3533" data-mp="3hyi25336kkkv">
                <div className="min-w-0" data-id="element-3534" data-mp="3d06rwr8z4824">
                  <p className="text-[10px] font-bold uppercase tracking-wider text-slate-400" data-id="element-3535" data-mp="2ltjd69bj5773">
                    Talab address
                  </p>
                  <p className="text-sm text-slate-500 truncate mt-0.5" data-id="element-3536" data-mp="2qrb6n7rtp1de">
                    {talabUrl}
                  </p>
                </div>
                <button onClick={() => copy('talab', `https://${talabUrl}`)} className="p-2 rounded-lg text-slate-400 hover:text-primary-indigo hover:bg-white transition-colors duration-150 ease-out" aria-label="Copy Talab link" data-id="element-3537" data-mp="2h2t7euz6z2bv">
                  {copied === 'talab' ? <CheckIcon className="w-4 h-4 text-success" data-id="element-3538" data-mp="1u3oo8drl3jiy" /> : <CopyIcon className="w-4 h-4" data-id="element-3539" data-mp="1y5061x47v0gl" />}
                </button>
              </div>

              <div className="flex flex-wrap items-center gap-2 mt-4" data-id="element-3540" data-mp="3ohpj6nk0jmdr">
                <a href={shopHref} target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl bg-white border border-slate-200 text-sm font-semibold text-slate-700 hover:border-primary-indigo/50 hover:text-primary-indigo transition-colors duration-150 ease-out" data-id="element-3541" data-mp="3si9j1yvz143l">
                  <ExternalLinkIcon className="w-4 h-4" data-id="element-3542" data-mp="1lt7gr09jvujk" />
                  Open shop
                </a>
                <button onClick={() => copy('primary', `https://${clean}`)} className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl bg-white border border-slate-200 text-sm font-semibold text-slate-700 hover:border-primary-indigo/50 hover:text-primary-indigo transition-colors duration-150 ease-out" data-id="element-3543" data-mp="20hu8k1gd6ero">
                  {copied === 'primary' ? <CheckIcon className="w-4 h-4 text-success" data-id="element-3544" data-mp="f6bzayzla7vw" /> : <CopyIcon className="w-4 h-4" data-id="element-3545" data-mp="a6zeskw6xcwh" />}
                  Copy link
                </button>
                <button onClick={() => {
              setPhase('form');
              setChecks(EMPTY_CHECKS);
            }} className="px-4 py-2.5 rounded-xl text-sm font-semibold text-slate-500 hover:text-primary-indigo hover:bg-slate-50 transition-colors duration-150 ease-out" data-id="element-3546" data-mp="1lne9rcydz1l2">
                  Change domain
                </button>
              </div>

              <AdvancedPanel open={advanced} onToggle={() => setAdvanced(v => !v)} record={record} checks={checks} busy={false} onCheck={runChecks} copied={copied} onCopy={copy} data-id="element-3547" data-mp="24f7172xt4eqo" />

              <div className="flex items-center justify-end mt-6" data-id="element-3548" data-mp="2o0tpbnbo4ncp">
                <button onClick={onClose} className="px-5 py-2.5 rounded-xl bg-primary-indigo text-white text-sm font-semibold hover:bg-indigo-700 transition-colors duration-150 ease-out" data-id="element-3549" data-mp="2o36f2mno420b">
                  Done
                </button>
              </div>
            </div> : (/* --------------------------------------------- Choose + connect */
        <div data-id="element-3550" data-mp="34lawlxis3kvb">
              <div className="space-y-2.5" data-id="element-3551" data-mp="34lsqwolvrp0l">
                <button onClick={() => {
              setMode('talab');
              setError('');
            }} className={`w-full text-left p-4 rounded-2xl border transition-colors duration-150 ease-out ${mode === 'talab' ? 'border-primary-indigo bg-primary-indigo/[0.04]' : 'border-slate-200 hover:border-slate-300'}`} data-id="element-3552" data-mp="34mlyfrsnyijs">
                  <p className="text-sm font-bold text-dark-navy" data-id="element-3553" data-mp="3o8ql5fgnthgw">
                    Use my Talab link
                  </p>
                  <p className="text-xs text-slate-500 mt-1" data-id="element-3554" data-mp="3s90tuzvu6lzg">
                    {talabUrl} — ready immediately, nothing to set up.
                  </p>
                </button>
                <button onClick={() => setMode('own')} className={`w-full text-left p-4 rounded-2xl border transition-colors duration-150 ease-out ${mode === 'own' ? 'border-primary-indigo bg-primary-indigo/[0.04]' : 'border-slate-200 hover:border-slate-300'}`} data-id="element-3555" data-mp="1x7yqh7zb5cll">
                  <p className="text-sm font-bold text-dark-navy" data-id="element-3556" data-mp="224yvdfkf89b4">
                    Use my own domain
                  </p>
                  <p className="text-xs text-slate-500 mt-1" data-id="element-3557" data-mp="2bx4ncpaoaqqb">
                    Your shop opens on your own web address.
                  </p>
                </button>
              </div>

              {mode === 'own' && <div className="mt-4" data-id="element-3558" data-mp="2kykzb1h4z0wn">
                  <label htmlFor="shop-domain" className="text-sm font-semibold text-dark-navy" data-id="element-3559" data-mp="2kvgmxgtioyuw">
                    Your web address
                  </label>
                  <input id="shop-domain" value={domain} onChange={e => {
              setDomain(e.target.value);
              setError('');
              if (phase !== 'form') {
                setPhase('form');
                setChecks(EMPTY_CHECKS);
              }
            }} disabled={phase === 'checking'} spellCheck={false} placeholder="shop.yourbusiness.com" className={`mt-2 w-full px-4 py-2.5 bg-white border rounded-xl text-sm outline-none transition-[border-color,box-shadow] duration-150 ease-out disabled:bg-slate-50 disabled:text-slate-400 ${error ? 'border-error/60 focus:ring-4 focus:ring-error/10' : 'border-slate-200 focus:border-primary-indigo/50 focus:ring-4 focus:ring-primary-indigo/10'}`} data-id="element-3560" data-mp="326vn4ggjh8xk" />
                  {error ? <p className="text-xs font-semibold text-error mt-2" data-id="element-3561" data-mp="cov8d0l3i1cb">
                      {error}
                    </p> : <p className="text-xs text-slate-400 mt-2 leading-relaxed" data-id="element-3562" data-mp="3f51u8nb9gnoh">
                      Enter the web address you want customers to use. We’ll
                      check the connection automatically.
                    </p>}

                  {/* ------------------------------------- Friendly progress */}
                  <AnimatePresence data-id="element-3563" data-mp="316v2zq0thlxs">
                    {phase === 'checking' && <motion.div initial={{
                opacity: 0,
                y: -4
              }} animate={{
                opacity: 1,
                y: 0
              }} exit={{
                opacity: 0
              }} transition={{
                duration: 0.18,
                ease: 'easeOut'
              }} className="flex items-center gap-2.5 mt-3 p-3.5 rounded-xl bg-primary-indigo/[0.05] border border-primary-indigo/15" data-id="element-3564" data-mp="2we21rc4k4vqg">
                        <Loader2Icon className="w-4 h-4 text-primary-indigo animate-spin flex-shrink-0" data-id="element-3565" data-mp="11zenqibs6lsf" />
                        <p className="text-xs font-semibold text-dark-navy" data-id="element-3566" data-mp="16wwxgaxmh51h">
                          {statusLine}
                        </p>
                      </motion.div>}
                  </AnimatePresence>

                  {(phase === 'notfound' || phase === 'taken') && <div className="mt-3 p-4 rounded-xl bg-warning/[0.07] border border-warning/25" data-id="element-3567" data-mp="j61253tyjrph">
                      <p className="inline-flex items-center gap-2 text-xs font-bold text-dark-navy" data-id="element-3568" data-mp="o0x1yq5frjd5">
                        <AlertCircleIcon className="w-4 h-4 text-warning" data-id="element-3569" data-mp="3mmzzugqbnemf" />
                        {phase === 'taken' ? 'This domain is already connected to another shop.' : 'We can’t find the domain connection yet.'}
                      </p>
                      <p className="text-xs text-slate-600 mt-1.5 leading-relaxed" data-id="element-3570" data-mp="3fjjnjtazy733">
                        {phase === 'taken' ? 'Disconnect it there first, or use a different address.' : 'Your domain update hasn’t appeared yet. This can sometimes take a little time.'}
                      </p>
                      <div className="flex flex-wrap items-center gap-2 mt-3" data-id="element-3571" data-mp="2wnjs28it8ler">
                        <button onClick={runChecks} className="px-3 py-2 rounded-lg bg-white border border-slate-200 text-xs font-bold text-slate-700 hover:border-primary-indigo/50 hover:text-primary-indigo transition-colors duration-150 ease-out" data-id="element-3572" data-mp="3am9pjp9jkn3n">
                          Check again
                        </button>
                        {phase === 'notfound' && <button onClick={() => setAdvanced(true)} className="px-3 py-2 rounded-lg text-xs font-bold text-slate-500 hover:text-primary-indigo transition-colors duration-150 ease-out" data-id="element-3573" data-mp="22tj9mmfu6sbd">
                            View setup instructions
                          </button>}
                      </div>
                    </div>}

                  <AdvancedPanel open={advanced} onToggle={() => setAdvanced(v => !v)} record={record} checks={checks} busy={phase === 'checking'} onCheck={runChecks} copied={copied} onCopy={copy} data-id="element-3574" data-mp="2qkos79d5vypd" />

                  {/* Quiet help, for merchants who'd rather not do this alone */}
                  <div className="flex items-center justify-between gap-3 mt-4 pt-4 border-t border-slate-100" data-id="element-3575" data-mp="1a66b0q9t97ts">
                    <p className="text-xs text-slate-400" data-id="element-3576" data-mp="15fpxz79wjo7y">
                      Need help connecting your domain?
                    </p>
                    <button className="inline-flex items-center gap-1.5 text-xs font-bold text-slate-500 hover:text-primary-indigo transition-colors duration-150 ease-out" data-id="element-3577" data-mp="10h4yxb6dzal3">
                      <LifeBuoyIcon className="w-3.5 h-3.5" data-id="element-3578" data-mp="1t5aihulpetjc" />
                      Let Talab help me
                    </button>
                  </div>
                </div>}

              <div className="flex items-center justify-end gap-2 mt-6" data-id="element-3579" data-mp="1j6bj72mtwa90">
                <button onClick={onClose} className="px-4 py-2.5 rounded-xl bg-white border border-slate-200 text-sm font-semibold text-slate-600 hover:border-slate-300 transition-colors duration-150 ease-out" data-id="element-3580" data-mp="1j8pad0ee12ba">
                  Cancel
                </button>
                {mode === 'talab' ? <button onClick={() => onSave('')} className="px-5 py-2.5 rounded-xl bg-primary-indigo text-white text-sm font-semibold hover:bg-indigo-700 transition-colors duration-150 ease-out" data-id="element-3581" data-mp="2fer0lbuf0wvw">
                    Save
                  </button> : <button onClick={connect} disabled={phase === 'checking'} className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl bg-primary-indigo text-white text-sm font-semibold hover:bg-indigo-700 disabled:opacity-60 transition-colors duration-150 ease-out" data-id="element-3582" data-mp="2t6xib2d7vbxg">
                    {phase === 'checking' ? <>
                        <Loader2Icon className="w-4 h-4 animate-spin" data-id="element-3583" data-mp="1cqukymaw2qvg" />
                        Checking…
                      </> : <>
                        <GlobeIcon className="w-4 h-4" data-id="element-3584" data-mp="11iz2a5zp53kr" />
                        Connect domain
                      </>}
                  </button>}
              </div>
            </div>)}
        </div>
      </motion.div>
    </div>;
}

/* ------------------------------------------------------- Advanced section */

function AdvancedPanel({
  open,
  onToggle,
  record,
  checks,
  busy,
  onCheck,
  copied,
  onCopy
}: {
  open: boolean;
  onToggle: () => void;
  record: DnsRecord;
  checks: Checks;
  busy: boolean;
  onCheck: () => void;
  copied: string;
  onCopy: (key: string, text: string) => void;
}) {
  const all = `Type: ${record.type}\nName / Host: ${record.host}\nPoints to: ${record.value}`;
  return <div className="mt-4" data-id="element-3585" data-mp="1z5elpdboobay">
      <button onClick={onToggle} aria-expanded={open} className="inline-flex items-center gap-1.5 text-xs font-bold text-primary-indigo hover:text-indigo-700 transition-colors duration-150 ease-out" data-id="element-3586" data-mp="1z4mx4mgi7du6">
        <ChevronDownIcon className={`w-4 h-4 transition-transform duration-150 ease-out ${open ? 'rotate-180' : ''}`} data-id="element-3587" data-mp="2i2px28otw9rc" />
        Set up DNS manually
      </button>

      <AnimatePresence initial={false} data-id="element-3588" data-mp="29anw89aukrgj">
        {open && <motion.div initial={{
        opacity: 0,
        height: 0
      }} animate={{
        opacity: 1,
        height: 'auto'
      }} exit={{
        opacity: 0,
        height: 0
      }} transition={{
        duration: 0.2,
        ease: [0.23, 1, 0.32, 1]
      }} className="overflow-hidden" data-id="element-3589" data-mp="29b609s3r49z0">
            <div className="mt-3 p-4 rounded-2xl bg-slate-50 border border-slate-100" data-id="element-3590" data-mp="2n2b2n1yi7gd1">
              <p className="text-sm font-bold text-dark-navy" data-id="element-3591" data-mp="1glz4h0volyhg">
                Manual DNS setup
              </p>
              <p className="text-xs text-slate-500 mt-1 leading-relaxed" data-id="element-3592" data-mp="1bmcrmsjrmk96">
                If your IT team manages your domain, use these details to
                connect it manually.
              </p>

              <div className="mt-3.5 rounded-xl bg-white border border-slate-200 overflow-hidden" data-id="element-3593" data-mp="1i3nzhr1j09bi">
                <RecordRow label="Type" value={record.type} copyKey="type" copied={copied} onCopy={onCopy} data-id="element-3594" data-mp="143widhfpqcta" />
                <RecordRow label="Name / Host" value={record.host} copyKey="host" copied={copied} onCopy={onCopy} data-id="element-3595" data-mp="ucik1czhzj1q" />
                <RecordRow label="Points to" value={record.value} copyKey="value" copied={copied} onCopy={onCopy} last data-id="element-3596" data-mp="legeql8qe48n" />
              </div>

              <button onClick={() => onCopy('all', all)} className="mt-2.5 inline-flex items-center gap-1.5 text-[11px] font-bold text-slate-500 hover:text-primary-indigo transition-colors duration-150 ease-out" data-id="element-3597" data-mp="ge236t7x8c5v">
                {copied === 'all' ? <CheckIcon className="w-3.5 h-3.5 text-success" data-id="element-3598" data-mp="6qgbgjl1ajb5" /> : <CopyIcon className="w-3.5 h-3.5" data-id="element-3599" data-mp="1qk72j8fcpv2" />}
                Copy all details
              </button>

              <p className="text-[10px] font-bold uppercase tracking-wider text-slate-400 mt-5" data-id="element-3600" data-mp="fpjuxoo450q6">
                Connection status
              </p>
              <dl className="mt-2 space-y-1.5" data-id="element-3601" data-mp="pgo9in9vmjkh">
                <CheckRow label="DNS record" state={checks.dns} doneLabel="Detected" data-id="element-3602" data-mp="pfcwrxyge1h8" />
                <CheckRow label="Domain ownership" state={checks.ownership} doneLabel="Verified" data-id="element-3603" data-mp="yh2xjue86cro" />
                <CheckRow label="SSL certificate" state={checks.ssl} doneLabel="Active" data-id="element-3604" data-mp="udo5u6gxc7ye" />
                <CheckRow label="Shop connection" state={checks.shop} doneLabel="Ready" data-id="element-3605" data-mp="2swj7eu4f9rt2" />
              </dl>

              <button onClick={onCheck} disabled={busy} className="mt-4 inline-flex items-center gap-2 px-3.5 py-2 rounded-lg bg-white border border-slate-200 text-xs font-bold text-slate-700 hover:border-primary-indigo/50 hover:text-primary-indigo disabled:opacity-60 transition-colors duration-150 ease-out" data-id="element-3606" data-mp="32nvznmu4ujzm">
                {busy && <Loader2Icon className="w-3.5 h-3.5 animate-spin" data-id="element-3607" data-mp="2izerhc166skp" />}
                {busy ? 'Checking…' : 'Check connection'}
              </button>
            </div>
          </motion.div>}
      </AnimatePresence>
    </div>;
}
function RecordRow({
  label,
  value,
  copyKey,
  copied,
  onCopy,
  last
}: {
  label: string;
  value: string;
  copyKey: string;
  copied: string;
  onCopy: (key: string, text: string) => void;
  last?: boolean;
}) {
  return <div className={`flex items-center justify-between gap-3 px-3.5 py-2.5 ${last ? '' : 'border-b border-slate-100'}`} data-id="element-3608" data-mp="1eqepjp3xngsh">
      <div className="min-w-0" data-id="element-3609" data-mp="14ztm9odbdo38">
        <p className="text-[10px] font-bold uppercase tracking-wider text-slate-400" data-id="element-3610" data-mp="14yqu7d499flq">
          {label}
        </p>
        <p className="text-sm font-mono text-dark-navy truncate mt-0.5" data-id="element-3611" data-mp="101icteqxlflj">
          {value}
        </p>
      </div>
      <button onClick={() => onCopy(copyKey, value)} aria-label={`Copy ${label}`} className="p-2 rounded-lg text-slate-400 hover:text-primary-indigo hover:bg-slate-50 transition-colors duration-150 ease-out flex-shrink-0" data-id="element-3612" data-mp="v0b2p91bg4bp">
        {copied === copyKey ? <CheckIcon className="w-3.5 h-3.5 text-success" data-id="element-3613" data-mp="2ldjqcv4foccv" /> : <CopyIcon className="w-3.5 h-3.5" data-id="element-3614" data-mp="2gep4tlf309p2" />}
      </button>
    </div>;
}
function CheckRow({
  label,
  state,
  doneLabel
}: {
  label: string;
  state: CheckState;
  doneLabel: string;
}) {
  return <div className="flex items-center justify-between gap-3" data-id="element-3615" data-mp="34b3qogqz76kt">
      <dt className="text-xs text-slate-600" data-id="element-3616" data-mp="2qj5nemjy44wk">{label}</dt>
      <dd className={`inline-flex items-center gap-1.5 text-xs font-bold ${state === 'done' ? 'text-success' : state === 'pending' ? 'text-primary-indigo' : 'text-slate-400'}`} data-id="element-3617" data-mp="2qkqg06mp9drs">
        {state === 'done' ? <>
            <CheckIcon className="w-3.5 h-3.5" data-id="element-3618" data-mp="3n8wgchcl9k09" />
            {doneLabel}
          </> : state === 'pending' ? <>
            <Loader2Icon className="w-3.5 h-3.5 animate-spin" data-id="element-3619" data-mp="398lq9gj3s9g5" />
            Checking
          </> : 'Waiting'}
      </dd>
    </div>;
}